import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { ApiError, download } from '../api/client'
import { endpoints, type ActivationCode } from '../api/endpoints'
import { Icon } from '../components/Icon'
import { LinkCard, PageHeader } from '../components/Page'
import { errorMessage, t } from '../strings'
import { LogoCard } from './LogoCard'

const dateTime = new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' })
const time = new Intl.DateTimeFormat('id-ID', { timeStyle: 'short' })
const when = (iso: string | null) => (iso ? dateTime.format(new Date(iso)) : t.never)

export function DevicesPage() {
  const qc = useQueryClient()
  const devices = useQuery({ queryKey: ['devices'], queryFn: endpoints.devices })
  const [code, setCode] = useState<ActivationCode | null>(null)
  const [error, setError] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: ({ kind, ack }: { kind: 'normal' | 'replace'; ack: boolean }) => endpoints.createActivationCode(kind, ack),
    onSuccess: async (c) => {
      setCode(c)
      setError(null)
      await qc.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (e) => setError(errorMessage(e instanceof ApiError ? e.code : 'network')),
  })
  const hasActive = devices.data?.some((d) => d.status === 'active') ?? false

  return (
    <>
      <PageHeader title={t.devicesTitle} subtitle={t.devicesSub} />
      {devices.data?.length === 0 && <p className="card muted">{t.devicesEmpty}</p>}
      <ul className="list device-grid">
        {devices.data?.map((d) => (
          <li key={d.id} className="card device-card">
            <span className="device-icon">
              <Icon name="phone" />
            </span>
            <div className="device-body">
              <div className="row">
                <strong>HP {d.letter}</strong>
                <span className={`badge ${d.status}`}>{d.status === 'active' ? t.deviceActive : t.deviceRevoked}</span>
              </div>
              {d.model && <div className="muted small">{d.model}</div>}
              <div className="muted small">
                {t.lastSync}: {when(d.last_synced_at)}
              </div>
              {d.status === 'active' && (
                <div className="muted small">
                  {d.pending_count === null || !d.pending_reported_at
                    ? t.pendingUnknown
                    : t.pendingReport(d.pending_count, time.format(new Date(d.pending_reported_at)))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {code ? (
        <section className="card code-card" aria-live="polite">
          <div className="code">{code.code.replace(/(.{4})/, '$1 ')}</div>
          <p>{t.codeValidUntil(time.format(new Date(code.expires_at)))}</p>
        </section>
      ) : hasActive ? (
        <Replace pending={create.isPending} onReplace={(ack) => create.mutate({ kind: 'replace', ack })} />
      ) : (
        <button className="cta" disabled={create.isPending} onClick={() => create.mutate({ kind: 'normal', ack: false })}>
          {t.newCode}
        </button>
      )}
      {error && <p className="error">{error}</p>}
    </>
  )
}

/**
 * ADR-0003 clauses 4-5: a normal replacement when the old phone just reported nothing unsent;
 * otherwise the owner reads what will be missing and confirms, and the old phone stops at once.
 */
function Replace({ pending, onReplace }: { pending: boolean; onReplace: (ack: boolean) => void }) {
  const info = useQuery({ queryKey: ['replacement-info'], queryFn: endpoints.replacementInfo })
  const [ack, setAck] = useState(false)
  if (!info.data) return null
  const a = info.data.active
  if (info.data.safe_to_replace) {
    return (
      <section className="card">
        <p>{t.replaceSafe}</p>
        <button className="secondary" disabled={pending} onClick={() => onReplace(false)}>
          {t.replaceCode}
        </button>
      </section>
    )
  }
  return (
    <section className="card warn-card">
      <h2>{t.replaceNotSafeTitle}</h2>
      <p>{t.replaceNotSafe(a?.last_receipt_no ?? null, when(a?.last_synced_at ?? null))}</p>
      <p className="muted small">{t.replaceTip}</p>
      <button className="link" onClick={() => void info.refetch()}>
        {t.refresh}
      </button>
      <label className="check">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> {t.replaceAck}
      </label>
      <button className="danger" disabled={!ack || pending} onClick={() => onReplace(true)}>
        {t.replaceCode}
      </button>
      <p className="muted small">{t.replaceRecover}</p>
    </section>
  )
}

/** Pengaturan: the outlet's details and logo on the left, the other places to change things on the right. */
export function SettingsPage() {
  const qc = useQueryClient()
  const biz = useQuery({ queryKey: ['business'], queryFn: endpoints.business })
  const save = useMutation({
    mutationFn: endpoints.updateOutlet,
    onSuccess: (b) => qc.setQueryData(['business'], b),
  })
  const [rawError, setRawError] = useState<string | null>(null)
  if (!biz.data) return <p>{t.loading}</p>
  const o = biz.data.outlet

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    save.mutate({
      address: String(form.get('address') ?? ''),
      phone: String(form.get('phone') ?? ''),
      receipt_footer: String(form.get('receipt_footer') ?? ''),
    })
  }

  return (
    <>
      <PageHeader title={t.settingsTitle} subtitle={t.settingsSub} />
      <div className="two-col">
        <div className="stack">
          <form className="card" onSubmit={submit}>
            <h2>{t.outletInfo}</h2>
            <p className="muted small">{t.outletInfoHint}</p>
            <label>
              {t.address}
              <input name="address" defaultValue={o.address} maxLength={200} />
            </label>
            <label>
              {t.outletPhone}
              <input name="phone" inputMode="tel" defaultValue={o.phone} maxLength={30} />
            </label>
            <label>
              {t.receiptFooter}
              <input name="receipt_footer" defaultValue={o.receipt_footer} maxLength={200} />
            </label>
            <button type="submit" className="cta" disabled={save.isPending}>
              {t.save}
            </button>
            {save.isSuccess && <p className="ok">{t.saved}</p>}
          </form>
          <LogoCard />
        </div>

        <div className="stack">
          <section className="card">
            <h2>{t.settingsMore}</h2>
            <div className="link-list">
              <LinkCard to="/pengaturan/kasir" icon="settings" title={t.kasirLink} hint={t.kasirLinkHint} />
              <LinkCard to="/pengaturan/riwayat" icon="history" title={t.historyTitle} hint={t.historyLinkHint} />
              <LinkCard to="/langganan" icon="card" title={t.subTitle} hint={t.subLinkHint} />
            </div>
          </section>
          <section className="card">
            <h2>{t.rawExportTitle}</h2>
            <p className="muted small">{t.rawExportBody}</p>
            <button
              className="secondary"
              onClick={() => {
                setRawError(null)
                download('/v1/exports/raw.zip', `gammapos-data-${o.code}.zip`).catch((e: unknown) =>
                  setRawError(errorMessage(e instanceof ApiError ? e.code : 'network')),
                )
              }}
            >
              <Icon name="download" size={20} />
              {t.rawExportButton}
            </button>
            {rawError && <p className="error">{rawError}</p>}
          </section>
        </div>
      </div>
    </>
  )
}
